import { Outlet, redirect } from 'react-router-dom'
import type { RouteFactory } from '@/routing/types'
import { MedSummaryTabs } from '@/medical-billing-summaries/constants'
import MedSummaryAssetRoot from '@/medical-billing-summaries/asset/components/MedSummaryAssetRoot'
import MedSummaryAssetLayout from '@/medical-billing-summaries/asset/components/MedSummaryAssetLayout'
import MedSummaryAssetEntriesTab from '@/medical-billing-summaries/asset/components/MedSummaryAssetEntriesTab'
import AddFilesPage from '@/medical-billing-summaries/asset/components/add-files/AddFilesPage'
import SourceFilesTab from '@/medical-billing-summaries/asset/components/source-files/SourceFilesTab'
import { MedSummaryAssetRouteSegments, MedSummaryAssetTabs } from '@/medical-billing-summaries/asset/constants'
import BrandAccessGuard from '@/components/guards/brand-access-guard'
import { BrandEnum } from '@/firebase/auth/auth-jwt-schema'

/**
 * Creates billing summary routes for nesting under MatterLayout.
 * URL pattern: /dashboard/m/:matterId/billing-summaries/...
 *
 * The billing-summaries index is the matter's living single-matter billing summary (one per
 * matter) with an Add Files sub-page. Haiku is excluded (not on the new files pipeline). Legacy
 * create/list deep links redirect to the living summary so old URLs don't dead-end.
 *
 * @see MatterLayout for parent route context
 */
export const createBillingSummaryChildRoutes: RouteFactory = () => [
  {
    path: 'billing-summaries',
    element: <BrandAccessGuard excludedBrands={[BrandEnum.HAIKU]} />,
    children: [
      {
        element: (
          <MedSummaryAssetRoot>
            <Outlet />
          </MedSummaryAssetRoot>
        ),
        children: [
          // The living billing summary (tabbed shell)
          {
            element: <MedSummaryAssetLayout />,
            children: [
              { index: true, loader: () => redirect(MedSummaryAssetTabs.ALL_ENTRIES) },
              { path: MedSummaryAssetTabs.ALL_ENTRIES, element: <MedSummaryAssetEntriesTab /> },
              { path: MedSummaryAssetTabs.SOURCE_FILES, element: <SourceFilesTab /> },
            ],
          },
          // Stage files and generate/update the billing summary
          { path: MedSummaryAssetRouteSegments.ADD_FILES, element: <AddFilesPage /> },
          // Legacy create/list deep links redirect to the living summary so old URLs don't dead-end
          { path: MedSummaryTabs.CREATE_NEW, loader: () => redirect('..') },
          { path: MedSummaryTabs.LIST, loader: () => redirect('..') },
        ],
      },
    ],
  },
]
